/**
 * Colecao local da previa: itens criados, ajustados ou removidos pela pessoa
 * SEM chamar rede, sobrepostos ao que a API devolveu.
 *
 * Tres camadas, nesta ordem: `locais` (criados aqui) vem antes da base;
 * `ajustes` (pausar, retomar, editar) sao aplicados por id sobre qualquer um
 * dos dois; `removidos` some da lista. O `useApiResource` recarrega a base a
 * cada 30 s e as tres camadas sobrevivem ao refetch porque sao chaveadas por
 * id -- e o que impede o envio pausado de "despausar" sozinho quando o dado
 * volta do servidor.
 */

import { useCallback, useState } from 'react';

export interface ColecaoLocal<T extends { readonly id: string }> {
  readonly locais: readonly T[];
  readonly adicionar: (item: T) => void;
  readonly ajustar: (id: string, patch: Partial<T>) => void;
  readonly remover: (id: string) => void;
  /** Base da API + locais + ajustes - removidos, sem repetir id. */
  readonly aplicar: (base: readonly T[]) => readonly T[];
}

export function useColecaoLocal<T extends { readonly id: string }>(): ColecaoLocal<T> {
  const [locais, setLocais] = useState<readonly T[]>([]);
  const [ajustes, setAjustes] = useState<Readonly<Record<string, Partial<T>>>>({});
  const [removidos, setRemovidos] = useState<readonly string[]>([]);

  const adicionar = useCallback((item: T): void => {
    setLocais((prev) => [item, ...prev]);
  }, []);

  const ajustar = useCallback((id: string, patch: Partial<T>): void => {
    setAjustes((prev) => ({ ...prev, [id]: { ...(prev[id] ?? {}), ...patch } }));
  }, []);

  const remover = useCallback((id: string): void => {
    setRemovidos((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const aplicar = useCallback(
    (base: readonly T[]): readonly T[] => {
      const vistos = new Set<string>();
      const saida: T[] = [];
      for (const item of [...locais, ...base]) {
        if (removidos.includes(item.id) || vistos.has(item.id)) {
          continue;
        }
        vistos.add(item.id);
        const ajuste = ajustes[item.id];
        saida.push(ajuste === undefined ? item : { ...item, ...ajuste });
      }
      return saida;
    },
    [locais, ajustes, removidos],
  );

  return { locais, adicionar, ajustar, remover, aplicar };
}
